<script setup>
import { reactive, ref, computed, onMounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useWindowSize } from '@vueuse/core';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { vOnClickOutside } from '@vueuse/components';
import { useAlert } from 'dashboard/composables';
import { ExceptionWithMessage } from 'shared/helpers/CustomErrors';
import { DuplicateContactException } from 'shared/helpers/CustomErrors';
import { debounce } from '@chatwoot/utils';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  createContactSearcher,
  createNewContact,
  fetchContactableInboxes,
  mergeInboxDetails,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';
import { openExistingContactOnPhoneDuplicate } from 'dashboard/components-next/Contacts/helpers/openExistingContactOnDuplicate';
import { useAccount } from 'dashboard/composables/useAccount';
import { useRouter } from 'vue-router';
import wootConstants from 'dashboard/constants/globals';
import ContactAPI from 'dashboard/api/contacts';
import MessageAPI from 'dashboard/api/inbox/message';
import {
  findReusableConversationForInbox,
  resolveDefaultInbox,
} from 'dashboard/components-next/NewConversation/helpers/conversationReuseHelper';

import ComposeNewConversationForm from 'dashboard/components-next/NewConversation/components/ComposeNewConversationForm.vue';

const props = defineProps({
  alignPosition: {
    type: String,
    default: 'left',
  },
  contactId: {
    type: String,
    default: null,
  },
  isModal: {
    type: Boolean,
    default: false,
  },
  // Abre o painel quando true (clique no nome → compose com contato)
  externalOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const searchContacts = createContactSearcher();
const store = useStore();
const { t } = useI18n();
const router = useRouter();
const { accountId } = useAccount();
const { width: windowWidth } = useWindowSize();

const { fetchSignatureFlagFromUISettings } = useUISettings();

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const viewInModal = computed(() => props.isModal || isSmallScreen.value);

const contacts = ref([]);
const selectedContact = ref(null);
const targetInbox = ref(null);
// Conversas do contato selecionado: alimentam a pré-seleção de caixa (Recurso 1)
// e o guard anti-duplicata no envio (Recurso 3). Sem 2ª ida ao servidor.
const contactConversationsList = ref([]);
const isCreatingContact = ref(false);
const isFetchingInboxes = ref(false);
const isSearching = ref(false);
const showComposeNewConversation = ref(false);

const formState = reactive({
  message: '',
  subject: '',
  ccEmails: '',
  bccEmails: '',
  attachedFiles: [],
});

const clearFormState = () => {
  Object.assign(formState, {
    subject: '',
    ccEmails: '',
    bccEmails: '',
    attachedFiles: [],
  });
};

const contactById = useMapGetter('contacts/getContactById');
const contactsUiFlags = useMapGetter('contacts/getUIFlags');
const currentUser = useMapGetter('getCurrentUser');
const currentAccountId = useMapGetter('getCurrentAccountId');
const globalConfig = useMapGetter('globalConfig/get');
const uiFlags = useMapGetter('contactConversations/getUIFlags');
const messageSignature = useMapGetter('getMessageSignature');
const inboxesList = useMapGetter('inboxes/getInboxes');

const sendWithSignature = computed(() =>
  fetchSignatureFlagFromUISettings(targetInbox.value?.channelType)
);

const directUploadsEnabled = computed(
  () => globalConfig.value.directUploadsEnabled
);

const activeContact = computed(() => contactById.value(props.contactId));

const composePopoverClass = computed(() => {
  if (viewInModal.value) return '';

  return props.alignPosition === 'right'
    ? 'absolute ltr:left-0 ltr:right-[unset] rtl:right-0 rtl:left-[unset]'
    : 'absolute rtl:left-0 rtl:right-[unset] ltr:right-0 ltr:left-[unset]';
});

const onContactSearch = debounce(
  async query => {
    isSearching.value = true;
    contacts.value = [];
    try {
      const results = await searchContacts(query);
      // null means the request was aborted (a newer search is in-flight),
      if (results === null) return;
      contacts.value = results;
      isSearching.value = false;
    } catch (error) {
      isSearching.value = false;
      useAlert(t('COMPOSE_NEW_CONVERSATION.CONTACT_SEARCH.ERROR_MESSAGE'));
    }
  },
  400,
  false
);

const resetContacts = () => {
  contacts.value = [];
};

// A API de contato vem com include_contact_inboxes=false — sem este fetch
// o compose mostra "nenhuma caixa disponível" mesmo com Channel::Api ok.
const hydrateContactableInboxes = async contact => {
  if (!contact?.id) return [];
  isFetchingInboxes.value = true;
  try {
    const contactableInboxes = await fetchContactableInboxes(contact.id);
    const merged = mergeInboxDetails(contactableInboxes, inboxesList.value);
    if (selectedContact.value?.id === contact.id) {
      selectedContact.value = {
        ...selectedContact.value,
        contactInboxes: merged,
      };
    }
    return merged;
  } catch (error) {
    return [];
  } finally {
    isFetchingInboxes.value = false;
  }
};

// Busca as conversas do contato e pré-seleciona a caixa (aberta recente > Comercial).
// Não sobrescreve uma caixa já escolhida manualmente pelo agente.
const loadConversationsAndPreselect = async contactId => {
  try {
    const { data } = await ContactAPI.getConversations(contactId);
    contactConversationsList.value = data?.payload || [];
  } catch (error) {
    contactConversationsList.value = [];
  }

  if (targetInbox.value) return;

  const autoInbox = resolveDefaultInbox({
    conversations: contactConversationsList.value,
    contactInboxes: selectedContact.value?.contactInboxes || [],
  });
  if (autoInbox) targetInbox.value = autoInbox;
};

const handleSelectedContact = async ({ value, action, ...rest }) => {
  let contact;
  if (action === 'create') {
    isCreatingContact.value = true;
    try {
      contact = await createNewContact(value);
      isCreatingContact.value = false;
    } catch (error) {
      isCreatingContact.value = false;
      if (
        error instanceof DuplicateContactException &&
        error.data?.includes?.('phone_number')
      ) {
        await openExistingContactOnPhoneDuplicate({
          phoneNumber: value,
          accountId: accountId.value,
          router,
          t,
        });
        closeCompose();
      }
      return;
    }
  } else {
    contact = rest;
  }
  selectedContact.value = contact;
  contacts.value = [];
  if (contact?.id) {
    await hydrateContactableInboxes(contact);
    await loadConversationsAndPreselect(contact.id);
  }
};

const handleTargetInbox = inbox => {
  targetInbox.value = inbox;
  if (!inbox) clearFormState();
  resetContacts();
};

const clearSelectedContact = () => {
  selectedContact.value = null;
  targetInbox.value = null;
  contactConversationsList.value = [];
  clearFormState();
};

const closeCompose = () => {
  showComposeNewConversation.value = false;
  if (!props.contactId) {
    // If contactId is passed as prop
    // Then don't allow to remove the selected contact
    selectedContact.value = null;
  }
  targetInbox.value = null;
  resetContacts();
  emit('close');
};

const discardCompose = () => {
  clearFormState();
  formState.message = '';
  closeCompose();
};

// Guard anti-duplicata / histórico único: reusa conversa da mesma caixa
// (aberta ou resolvida) para texto simples. Anexo/template seguem create.
const tryReuseOpenConversation = async ({ payload, isFromWhatsApp }) => {
  const hasFiles = Array.isArray(payload?.files) && payload.files.length > 0;
  const content = payload?.message?.content;
  const hasTemplate = Boolean(payload?.message?.template_params);
  if (isFromWhatsApp || hasFiles || hasTemplate || !content) return null;

  const reusable = findReusableConversationForInbox(
    contactConversationsList.value,
    targetInbox.value?.id
  );
  if (!reusable) return null;

  await MessageAPI.create({
    conversationId: reusable.id,
    message: content,
  });
  return reusable;
};

const createConversation = async ({ payload, isFromWhatsApp }) => {
  try {
    const reused = await tryReuseOpenConversation({ payload, isFromWhatsApp });
    if (reused) {
      const accountId = reused.account_id || currentAccountId.value;
      const action = {
        type: 'link',
        to: `/app/accounts/${accountId}/conversations/${reused.id}`,
        message: t('COMPOSE_NEW_CONVERSATION.FORM.GO_TO_CONVERSATION'),
      };
      discardCompose();
      useAlert(t('COMPOSE_NEW_CONVERSATION.FORM.SUCCESS_MESSAGE'), action);
      return true;
    }

    const data = await store.dispatch('contactConversations/create', {
      params: payload,
      isFromWhatsApp,
    });
    const action = {
      type: 'link',
      to: `/app/accounts/${data.account_id}/conversations/${data.id}`,
      message: t('COMPOSE_NEW_CONVERSATION.FORM.GO_TO_CONVERSATION'),
    };
    discardCompose();
    useAlert(t('COMPOSE_NEW_CONVERSATION.FORM.SUCCESS_MESSAGE'), action);
    return true; // Return success
  } catch (error) {
    useAlert(
      error instanceof ExceptionWithMessage
        ? error.data
        : t('COMPOSE_NEW_CONVERSATION.FORM.ERROR_MESSAGE')
    );
    return false; // Return failure
  }
};

const toggle = () => {
  showComposeNewConversation.value = !showComposeNewConversation.value;
};

watch(
  () => props.externalOpen,
  open => {
    showComposeNewConversation.value = !!open;
  }
);

watch(
  activeContact,
  async (currentContact, previousContact) => {
    if (currentContact && props.contactId) {
      const contactChanged = currentContact?.id !== previousContact?.id;
      // Reset on contact change
      if (contactChanged) {
        clearSelectedContact();
        clearFormState();
        formState.message = '';
      }

      selectedContact.value = { ...currentContact, contactInboxes: [] };

      // Pré-seleciona a caixa apenas quando o contato muda (evita chamada em loop).
      if (contactChanged || !previousContact) {
        await hydrateContactableInboxes(currentContact);
        await loadConversationsAndPreselect(currentContact.id);
      }
    }
  },
  { immediate: true, deep: true }
);

const handleClickOutside = () => {
  if (!showComposeNewConversation.value) return;

  showComposeNewConversation.value = false;
  emit('close');
};

const onModalBackdropClick = () => {
  if (!viewInModal.value) return;
  handleClickOutside();
};

onMounted(() => resetContacts());

const keyboardEvents = {
  Escape: {
    action: () => {
      if (showComposeNewConversation.value) {
        showComposeNewConversation.value = false;
        emit('close');
        emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
      }
    },
  },
};

useKeyboardEvents(keyboardEvents);
</script>

<template>
  <div
    v-on-click-outside="[
      handleClickOutside,
      // Fixed and edge case https://github.com/chatwoot/chatwoot/issues/10785
      // This will prevent closing the compose conversation modal when the editor Create link popup is open
      { ignore: ['dialog.ProseMirror-prompt-backdrop'] },
    ]"
    class="relative"
    :class="{
      'z-50': showComposeNewConversation && !viewInModal,
    }"
  >
    <slot
      name="trigger"
      :is-open="showComposeNewConversation"
      :toggle="toggle"
    />
    <div
      v-if="showComposeNewConversation"
      :class="{
        'fixed z-50 bg-n-alpha-black1 backdrop-blur-[4px] flex items-start pt-[clamp(3rem,15vh,12rem)] justify-center inset-0':
          viewInModal,
      }"
      @click.self="onModalBackdropClick"
    >
      <ComposeNewConversationForm
        :form-state="formState"
        :class="[{ 'mt-2': !viewInModal }, composePopoverClass]"
        :contacts="contacts"
        :contact-id="contactId"
        :is-loading="isSearching"
        :current-user="currentUser"
        :selected-contact="selectedContact"
        :target-inbox="targetInbox"
        :is-creating-contact="isCreatingContact"
        :is-fetching-inboxes="isFetchingInboxes"
        :is-direct-uploads-enabled="directUploadsEnabled"
        :contact-conversations-ui-flags="uiFlags"
        :contacts-ui-flags="contactsUiFlags"
        :message-signature="messageSignature"
        :send-with-signature="sendWithSignature"
        @search-contacts="onContactSearch"
        @reset-contact-search="resetContacts"
        @update-selected-contact="handleSelectedContact"
        @update-target-inbox="handleTargetInbox"
        @clear-selected-contact="clearSelectedContact"
        @create-conversation="createConversation"
        @discard="discardCompose"
      />
    </div>
  </div>
</template>
